import { useMemo, type ReactNode } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { cn } from '../../cn';
import {
  INDENT_UNIT,
  TREE_NODE_ROW_CLASS,
  TreeKeyCell,
  useCollapsibleTree,
  type CollapsibleRow,
} from './json-tree';

export interface JsonKeyValueTableProps {
  value: unknown;
  className?: string;
  /** Text to show when the value is null/undefined/empty. Defaults to `-`. */
  emptyLabel?: ReactNode;
  indentPx?: number;
  ariaLabel?: string;
  /**
   * Chrome to render above the table, inside its border — copy, fullscreen,
   * whatever the panel decided.
   *
   * A dumb slot on purpose. The table renders the node and knows nothing about
   * it, which is what keeps the fullscreen button out of the dialog it opened:
   * `JsonPanel` renders this table twice and simply hands the inline instance a
   * different toolbar than the one inside the dialog. A toolbar OWNED by the
   * table could only manage that with a branch.
   */
  toolbar?: ReactNode;
}

type Row = CollapsibleRow & {
  keyLabel: string;
  display: ReactNode;
  /** Structural header (object/array); right column shows a placeholder. */
  isContainer: boolean;
};

/** Defense against circular references and pathological nesting. */
export const MAX_DEPTH = 64;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isContainer = (v: unknown) => Array.isArray(v) || isPlainObject(v);

/**
 * If `value` is a string that looks like a JSON object/array, parse it;
 * otherwise return it unchanged. Shared with `JsonPanel` so the copy-to-
 * clipboard output matches exactly what the table renders (a string payload
 * is shown as a tree, so copying must emit the parsed tree — not a second-
 * order-escaped `"{\"id\":42}"`).
 */
export const parseIfString = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

/**
 * One scalar, rendered the way every JSON table in the app renders it.
 *
 * Exported so `JsonDiffTable` shows the same value the same way: `null`, `""`
 * and a number's tabular alignment must not read differently just because the
 * value happens to sit in a comparison rather than a listing.
 */
export const formatScalar = (value: unknown): ReactNode => {
  if (value === null) return <span className="text-muted-foreground">null</span>;
  if (value === undefined) return <span className="text-muted-foreground">—</span>;
  if (typeof value === 'boolean') {
    return <span className="text-foreground">{value ? 'true' : 'false'}</span>;
  }
  if (typeof value === 'number' || typeof value === 'bigint') {
    return <span className="tabular-nums text-foreground">{String(value)}</span>;
  }
  if (typeof value === 'string') {
    if (value === '') return <span className="text-muted-foreground italic">""</span>;
    return <span className="text-foreground break-all whitespace-pre-wrap">{value}</span>;
  }
  return <span className="text-foreground break-all">{String(value)}</span>;
};

/** The `{5}` / `[3]` summary on a container row, shared by both JSON tables. */
export const containerSummary = (value: unknown): ReactNode => {
  if (Array.isArray(value)) {
    return value.length === 0 ? (
      <span className="text-muted-foreground">[]</span>
    ) : (
      <span className="text-muted-foreground text-[11px]">[{value.length}]</span>
    );
  }
  const count = isPlainObject(value) ? Object.keys(value).length : 0;
  return count === 0 ? (
    <span className="text-muted-foreground">{'{}'}</span>
  ) : (
    <span className="text-muted-foreground text-[11px]">{`{${count}}`}</span>
  );
};

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) return Object.keys(value).length === 0;
  if (typeof value === 'string' && value === '') return true;
  return false;
};

/**
 * Whether this value renders as an actual table.
 *
 * Exported because `JsonPanel` has to know: the two early returns below (empty
 * value, scalar root) produce a bare `<span>` with nowhere to put a toolbar,
 * so the panel places its chrome outside instead. One predicate, so the two
 * cannot drift into disagreeing about what counts as a table.
 */
export const rendersAsTable = (value: unknown): boolean => {
  const parsed = parseIfString(value);
  return isContainer(parsed) && !isEmpty(parsed);
};

/**
 * Recursively flatten `value` into table rows.
 *
 * `omitContainerRow` suppresses the current node's header row — used once at
 * the root so that a top-level object/array does not waste a row announcing
 * itself ("{5}" or "[3]"). Recursive calls always pass `false`.
 *
 * `seen` prevents infinite recursion on circular references. `depth >
 * MAX_DEPTH` also forces a truncated placeholder row, defending against
 * pathological nesting.
 *
 * The walk is unchanged from before folding existed: which rows come out,
 * their labels and their depths are identical. Only `id` became `path`, so a
 * folded subtree can be identified by the same address the diff table uses.
 */
const collect = (
  value: unknown,
  rows: Row[],
  depth: number,
  keyLabel: string,
  path: string,
  omitContainerRow: boolean,
  seen: WeakSet<object>,
) => {
  if (depth > MAX_DEPTH) {
    rows.push({
      path,
      keyLabel,
      depth,
      isContainer: false,
      display: <span className="text-muted-foreground italic">…</span>,
    });
    return;
  }

  if (isContainer(value)) {
    const obj = value as object;
    if (seen.has(obj)) {
      rows.push({
        path,
        keyLabel,
        depth,
        isContainer: false,
        display: <span className="text-muted-foreground italic">[circular]</span>,
      });
      return;
    }
    seen.add(obj);
  }

  if (Array.isArray(value)) {
    if (!omitContainerRow) {
      rows.push({ path, keyLabel, depth, isContainer: true, display: containerSummary(value) });
    }
    const childDepth = omitContainerRow ? depth : depth + 1;
    value.forEach((item, idx) => {
      collect(item, rows, childDepth, `[${idx}]`, `${path}[${idx}]`, false, seen);
    });
    return;
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (!omitContainerRow) {
      rows.push({ path, keyLabel, depth, isContainer: true, display: containerSummary(value) });
    }
    const childDepth = omitContainerRow ? depth : depth + 1;
    entries.forEach(([k, v]) => {
      collect(v, rows, childDepth, k, path ? `${path}.${k}` : k, false, seen);
    });
    return;
  }

  rows.push({
    path,
    keyLabel,
    depth,
    isContainer: false,
    display: formatScalar(value),
  });
};

/**
 * A lone payload carries no information about which parts matter, so every
 * container starts open — exactly what this table rendered before it could
 * fold at all. `JsonDiffTable` passes a different predicate because a diff DOES
 * know: it opens the subtrees that changed.
 *
 * Module-level so the identity is stable across renders and the memoised
 * visible-row list is not rebuilt on every keystroke elsewhere on the page.
 */
const NEVER_COLLAPSED = () => false;

export function JsonKeyValueTable({
  value,
  className,
  emptyLabel,
  indentPx = INDENT_UNIT,
  ariaLabel,
  toolbar,
}: JsonKeyValueTableProps) {
  const { t } = useResourceKit();
  const parsed = useMemo(() => parseIfString(value), [value]);
  const rows = useMemo<Row[]>(() => {
    if (!isContainer(parsed)) return [];
    const out: Row[] = [];
    collect(parsed, out, 0, '', '', true, new WeakSet());
    return out;
  }, [parsed]);
  const tree = useCollapsibleTree(rows, NEVER_COLLAPSED);

  // Empty values get a simple dash — unified across every call site. No table,
  // so no toolbar: copy and fullscreen have nothing to act on.
  if (isEmpty(parsed)) {
    return <span className="text-sm text-muted-foreground">{emptyLabel ?? '-'}</span>;
  }

  // Primitive root — render inline. Wrapping a single scalar in a table is
  // pure visual noise, and this matches the pre-refactor `<pre>value</pre>`.
  if (!isContainer(parsed)) {
    return (
      <span
        className={cn('font-mono text-xs text-foreground break-all whitespace-pre-wrap', className)}
      >
        {formatScalar(parsed)}
      </span>
    );
  }

  return (
    <div className={cn('overflow-hidden rounded-md border border-border bg-muted/30', className)}>
      {toolbar}
      <table
        className="w-full border-collapse text-xs"
        aria-label={ariaLabel ?? t('rk.json.tableAria')}
      >
        <tbody>
          {tree.visible.map((row, i) => {
            const hasChildren = tree.hasChildren(row);
            return (
              <tr
                key={row.path}
                data-path={row.path}
                className={cn(
                  i !== tree.visible.length - 1 && 'border-b border-border/60',
                  hasChildren && TREE_NODE_ROW_CLASS,
                )}
                onClick={hasChildren ? () => tree.toggle(row) : undefined}
              >
                <TreeKeyCell
                  depth={row.depth}
                  indentPx={indentPx}
                  expanded={!tree.isCollapsed(row)}
                  hasChildren={hasChildren}
                  label={row.keyLabel}
                >
                  <span className={cn(row.isContainer && 'text-foreground font-medium')}>
                    {row.keyLabel || <span className="text-muted-foreground/50">—</span>}
                  </span>
                </TreeKeyCell>
                <td className="align-top py-1.5 pl-3 pr-2 font-mono">{row.display}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default JsonKeyValueTable;
